import React, { useEffect, useContext, useState, useMemo } from "react";
import {
  GridContainer,
  GridItem,
  PeriodicIsoFormRootArea,
} from "./PeriodicIso.styles";
import {
  periodicIsoInitialValues,
  periodicIsoValidationSchema,
} from "./PeriodicIso.resources";
import { useFormik } from "formik";
import TextField from "../../textField/TextField";
import DateField from "../../dateField/DateField";
import { Button } from "@mui/material";
import { useDispatch } from "react-redux";
import { generateReport } from "./PeriodicIso.thunk";
import AuthContext from "../../../context/AuthContext";
import CurrencySelectField from "../../selectField/CurrencySelectField";
import PeriodicIsoResultModal from "./PeriodicIso.resultModal";
import moment from "moment";
import { useModal } from "../../modal";
import { useSelector } from "react-redux";
import AdminFields from "../../adminFields/AdminFields";
import {
  setApplyChanges,
  setWasUsed,
  setReset,
} from "../../adminFields/AdminFields.slice";
import { InputAdornment, IconButton } from "@mui/material";
import ListIcon from "@mui/icons-material/List";
import AdminFieldsModal from "../../adminFields/AdminFields.modal";
import { setIsoID } from "../../adminFields/AdminFields.slice";

const PeriodicIso = () => {
  const dispatch = useDispatch();
  const { token, acquirerData, userInfo } = useContext(AuthContext);
  const { isoID, currency_code: currency } = acquirerData;
  const { open } = useModal();
  const { status } = useSelector((state) => state.periodicIso);
  const {
    applyChanges,
    acquirerId,
    isoID: storeIsoID,
    wasUsed,
    reset,
    acquirerCurrencyId,
  } = useSelector((state) => state.adminFields);
  const [modalType, setModalType] = useState("iso");
  const [modalOpen, setModalOpen] = useState(false);

  const formInitialValues = useMemo(
    () => ({
      ...periodicIsoInitialValues,
      isoID: isoID ?? periodicIsoInitialValues.isoID,
      currency: currency ?? periodicIsoInitialValues.currency,
    }),
    [isoID, currency],
  );

  const handleOpenResultModal = () => {
    open({
      title: `Date range Report from ${moment(values.reportStartDate).format(
        "MM/DD/YYYY",
      )} to ${moment(values.reportEndDate).format("MM/DD/YYYY")}`,
      maxWidth: `lg`,
      fullWidth: true,
    });
  };

  const handleOpenModalISO = () => {
    setModalType("iso");
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setModalType(null);
  };

  const handleSelectISO = (selectedISO) => {
    dispatch(setIsoID(selectedISO.isoID));
    setFieldValue("isoID", selectedISO.isoID);
  };

  const {
    values,
    handleSubmit,
    isSubmitting,
    handleChange,
    handleBlur,
    errors,
    touched,
    setSubmitting,
    setFieldValue,
    setFieldTouched,
    validateForm,
  } = useFormik({
    initialValues: formInitialValues,
    validationSchema: periodicIsoValidationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    validateOnMount: false,
    enableReinitialize: true,
    onSubmit: (values) => {
      setSubmitting(true);
      dispatch(
        generateReport({
          isoID: wasUsed ? storeIsoID : values.isoID,
          currency: values.currency,
          reportStartDate: values.reportStartDate,
          reportEndDate: values.reportEndDate,
          token: token,
          acquirerData,
          onSuccessCallback: handleOpenResultModal,
          ...(wasUsed && { storeAcquirerId: acquirerId }),
        }),
      );
      setSubmitting(false);
    },
  });

  useEffect(() => {
    if (reset) {
      setFieldValue("isoID", isoID);
      setFieldValue("currency", currency);
      dispatch(setReset(false));
    }
  }, [reset, isoID, currency, setFieldValue, dispatch]);

  useEffect(() => {
    if (applyChanges) {
      setFieldValue("isoID", storeIsoID);
      console.log("acquirerCurrencyId ====>", acquirerCurrencyId);

      setFieldValue("currency", acquirerCurrencyId);
      dispatch(setApplyChanges(false));
      dispatch(setWasUsed(true));
    }
  }, [applyChanges, storeIsoID, acquirerCurrencyId, setFieldValue, dispatch]);

  useEffect(() => {
    if (wasUsed && storeIsoID === "" && acquirerId === "") {
      dispatch(setApplyChanges(false));
      dispatch(setWasUsed(false));
      setFieldValue("isoID", isoID);
      setFieldValue("currency", currency);
    }
  }, [
    storeIsoID,
    acquirerId,
    wasUsed,
    isoID,
    currency,
    setFieldValue,
    dispatch,
  ]);

  useEffect(() => {
    // Actualizar currency cuando cambie el acquirerData y no se haya usado AdminFields
    if (currency && !wasUsed) {
      if (values.currency === "" || values.currency !== currency) {
        setFieldValue("currency", currency);
      }
    }
  }, [currency, wasUsed, values.currency, setFieldValue]);

  useEffect(() => {
    // Force validation when date values change
    if (values.reportStartDate || values.reportEndDate) {
      validateForm();
    }
  }, [values, validateForm]);

  return (
    <PeriodicIsoFormRootArea>
      <AdminFields />
      <GridContainer component="form" onSubmit={handleSubmit}>
        <PeriodicIsoResultModal />
        {modalOpen && modalType && (
          <AdminFieldsModal
            type={modalType}
            open={modalOpen}
            onClose={handleCloseModal}
            onSelectISO={handleSelectISO}
          />
        )}
        <GridItem xs={12} sm={6} lg={3} padding="15px 15px 15px 0">
          <TextField
            id="isoID"
            name="isoID"
            fullWidth
            disabled={userInfo["acquirer-rol-data"] === "ISO"}
            label="ISO ID"
            value={values.isoID}
            onChange={handleChange}
            onBlur={handleBlur}
            required
            error={errors.isoID}
            touched={touched.isoID}
            InputProps={{
              endAdornment: userInfo["acquirer-rol-data"] !== "ISO" && (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="open ISO list"
                    onClick={handleOpenModalISO}
                  >
                    <ListIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </GridItem>
        <GridItem xs={12} sm={6} lg={3} padding="15px 15px 15px 0">
          <CurrencySelectField
            id="currency"
            name="currency"
            fullWidth
            label="Currency"
            value={values.currency}
            required
            maxLength={3}
            onChange={handleChange}
            onBlur={handleBlur}
            error={errors.currency}
            touched={touched.currency}
            disabled
          />
        </GridItem>
        <GridItem xs={12} sm={6} lg={3} padding="15px 15px 15px 0">
          <DateField
            id="reportStartDate"
            name="reportStartDate"
            fullWidth
            label="Report Start Date"
            showTimezoneLabel
            value={values.reportStartDate}
            onChange={handleChange}
            onBlur={handleBlur}
            setFieldTouched={setFieldTouched}
            required
            error={errors.reportStartDate}
            touched={touched.reportStartDate}
            minDate={moment().subtract(90, "days")}
            maxDate={moment().subtract(1, "day")}
            helperText="Select a date within the last 90 days"
          />
        </GridItem>
        <GridItem xs={12} sm={6} lg={3} padding="15px 15px 15px 0">
          <DateField
            id="reportEndDate"
            name="reportEndDate"
            fullWidth
            label="Report End Date"
            showTimezoneLabel
            value={values.reportEndDate}
            onChange={handleChange}
            onBlur={handleBlur}
            setFieldTouched={setFieldTouched}
            required
            error={errors.reportEndDate}
            touched={touched.reportEndDate}
            minDate={moment().subtract(90, "days")}
            maxDate={moment().subtract(1, "day")}
            helperText="Select a date within the last 90 days"
          />
        </GridItem>
        <GridContainer>
          <GridItem xs={12} sm={6} lg={4} padding="15px 15px 15px 0">
            <Button
              variant="contained"
              type="submit"
              size="large"
              disabled={isSubmitting || status === "loading"}
              loading={isSubmitting || status === "loading"}
              onClick={handleSubmit}
            >
              Generate
            </Button>
          </GridItem>
        </GridContainer>
      </GridContainer>
    </PeriodicIsoFormRootArea>
  );
};

export default PeriodicIso;
